<template>
  <v-container>
    <v-row v-if="loading">
      <v-col cols="12" align="center" justify="center">
        <v-row align="center" justify="center">
          <v-text-field color="success" loading disabled />
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="!loading">
      <v-col cols="12" class="scroll-panel">
        <div v-if="userCheckIns.length > 0">
          <div class="pb-4">
            <v-btn @click="all"> Show all check-ins </v-btn>
            <v-btn @click="panel = []" class="float-right">
              Close all check-ins
            </v-btn>
          </div>
        </div>
        <v-alert v-else type="info">
          No assessments
        </v-alert>
        <v-expansion-panels v-model="panel" multiple>
          <v-expansion-panel v-for="assessment in userCheckIns" :key="assessment.node.id">
            <v-expansion-panel-header class="title">
              <v-row>
                <v-col cols="6">
                  {{ assessmentDate(assessment) }}
                  <p>Assessment type: {{ assessment.node.type }}</p>
                  <span v-if="!assessment.node.weight" class="pl-4 error--text">(Incomplete)</span>
                </v-col>
                <v-col cols="6" class="pa-0 pr-5">
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Weight (kg)</th>
                          <th class="text-left">Abdomen (cm)</th>
                          <th class="text-left">Chest (cm)</th>
                          <th class="text-left">Hip (cm)</th>
                          <th class="text-left">Quad (cm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{{ assessment.node.weight }}</td>
                          <td>{{ assessment.node.abdomen }}</td>
                          <td>{{ assessment.node.chest }}</td>
                          <td>{{ assessment.node.hip }}</td>
                          <td>{{ assessment.node.quad }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content eager>
              <v-row>
                <v-col cols="12">
                  <div v-if="assessment.node.weight">
                    <h3>Change Since Previous Assessment</h3>
                    <v-simple-table>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">Weight (kg)</th>
                            <th class="text-left">Abdomen (cm)</th>
                            <th class="text-left">Chest (cm)</th>
                            <th class="text-left">Hip (cm)</th>
                            <th class="text-left">Quad (cm)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{{ assessment.node.deltas.weight }}</td>
                            <td>{{ assessment.node.deltas.abdomen }}</td>
                            <td>{{ assessment.node.deltas.chest }}</td>
                            <td>{{ assessment.node.deltas.hip }}</td>
                            <td>{{ assessment.node.deltas.quad }}</td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" class="subtitle-1">
                  <h3 class="d-inline">Photos</h3>
                  <span class="fake-link" @click="assessment.node.showPhotos = !assessment.node.showPhotos;">
                    (View/Hide)
                  </span>
                </v-col>
              </v-row>
              <v-row v-if="assessment.node.showPhotos">
                <v-col cols="4" align="center" justify="center">
                  <div v-if="assessment.node.imgBack">
                    <v-img class="grey lighten-2" :src="assessment.node.imgBack" />
                    <v-btn class="mt-4" @click="download(assessment.node.imgBack, `back`)">
                      Download <v-icon color="secondary">save_alt</v-icon>
                    </v-btn>
                  </div>
                  <div v-else>
                    <h4>No picture uploaded</h4>
                  </div>
                </v-col>
                <v-col cols="4" align="center" justify="center">
                  <div v-if="assessment.node.imgFront">
                    <v-img class="grey lighten-2" :src="assessment.node.imgFront" />
                    <v-btn class="mt-4" @click="download(assessment.node.imgFront, `front`)">
                      Download <v-icon color="secondary">save_alt</v-icon>
                    </v-btn>
                  </div>
                  <div v-else>
                    <h4>No picture uploaded</h4>
                  </div>
                </v-col>
                <v-col cols="4" align="center" justify="center">
                  <div v-if="assessment.node.imgSide">
                    <v-img class="grey lighten-2" :src="assessment.node.imgSide" />
                    <v-btn class="mt-4" @click="download(assessment.node.imgSide, `side`)">
                      Download
                      <v-icon color="secondary">save_alt</v-icon>
                    </v-btn>
                  </div>
                  <div v-else>
                    <h4>No picture uploaded</h4>
                  </div>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { CLIENT_ASSESSMENTS_QUERY } from "@/constants/graphql/assessments/assessment";
import { fancy_date } from "@/lib/fancy_date";
import FileDownloadMixin from "@/components/shared/mixins/FileDownloadMixin";

export default {
  name: "CoachingAssessment",
  mixins: [FileDownloadMixin],
  props: {
    userId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: true,
      panel: [],
    };
  },
  methods: {
    fancyDate(date) {
      return fancy_date(date, "ddd, D MMM YYYY");
    },
    assessmentDate(assessment) {
      return assessment.node.assessmentDate
        ? this.fancyDate(assessment.node.assessmentDate)
        : this.fancyDate(assessment.node.requiredDate);
    },
    all() {
      this.panel = [...this.userCheckIns.map((k, i) => i)];
    },
  },
  apollo: {
    userCheckIns: {
      query: CLIENT_ASSESSMENTS_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        return {
          userId: encoded_json_payload,
          decodedId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.loading = false;
        }
      },
      update(data) {
        data.userCheckIns.edges.sort((a, b) => {
          return a.node.assessmentDate > b.node.assessmentDate ? -1 : 1;
        });

        for (let i = 0; i < data.userCheckIns.edges.length; i++) {
          let nextIdx = i + 1;
          let assessment = data.userCheckIns.edges[i].node;

          let deltas = {
            weight: 0,
            abdomen: 0,
            chest: 0,
            hip: 0,
            quad: 0,
          };

          if (data.userCheckIns.edges.length > nextIdx) {
            let previous = data.userCheckIns.edges[nextIdx].node;
            deltas = {
              weight: (assessment.weight - previous.weight).toFixed(2),
              abdomen: (assessment.abdomen - previous.abdomen).toFixed(2),
              chest: (assessment.chest - previous.chest).toFixed(2),
              hip: (assessment.hip - previous.hip).toFixed(2),
              quad: (assessment.quad - previous.quad).toFixed(2),
            };
          }
          data.userCheckIns.edges[i].node.deltas = deltas;
          data.userCheckIns.edges[i].node.showPhotos = false;
        }

        return data.userCheckIns.edges;
      },
    },
  },
};
</script>
